import React, { FunctionComponent, MouseEvent, Fragment, useState, useEffect } from 'react';
import {
    Box,
    Collapse,
    Grid,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableHead,
    TableCell,
    TableRow,
    Typography,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    TextField,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import moment from 'moment';
import classNames from 'classnames';
import Zoom from '@material-ui/core/Zoom';
import { Delete } from '@material-ui/icons';
import { Autocomplete, Skeleton } from '@material-ui/lab';
import Tooltip from '@material-ui/core/Tooltip';
import CloseIcon from '@material-ui/icons/Close';
import gray from '@material-ui/core/colors/grey';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import AddCommentOutlinedIcon from '@material-ui/icons/AddCommentOutlined';
import { TimeSheetCard } from '../EditTimeSheet';
import TimePicker from '../../../../components/TimePicker';
import { Event } from '../../../../store/modules/calendar/types';
import InputTextComponent from '../../../../components/InputTextField';
import { TimeSheetCategory } from '../../../../store/modules/timeSheet/type';
import { getFullAddress } from '../../../../utils/utils';
import AutoCompleteSelect from '../../../../components/AutoCompleteSelect';
import { DropDownField } from '../../../../store/modules/application/types';

type TimeSheetEntryProps = {
    hourLimit: number;
    selectedDate?: string;
    readOnly: boolean;
    timeSheetAppointments: Event[];
    timeSheetEntries: TimeSheetCard[];
    timeEntryIdsWithComments: string[];
    isTimeSheetCategoriesLoading: boolean;
    isTimeSheetAppointmentsLoading: boolean;
    timeSheetCategories: TimeSheetCategory[];
    setIsMaxHourLimit: Function;
    handleTimeSheetEntry: Function;
    handleDeleteTimeSheetRow: Function;
};

const useStyles = makeStyles((theme) => ({
    title: {
        fontSize: 12,
        fontWeight: 600,
        color: '#0277BD',
        padding: 10,
    },
    tableHeaderCell: {
        fontSize: 14,
        color: '#484848',
        fontWeight: 500,
    },
    label: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        textAlign: 'right',
        marginRight: theme.spacing(2),
    },
    tableRow: {
        verticalAlign: 'top',
        cursor: 'pointer',
        '&:hover ': {
            backgroundColor: '#eeeeee',
            transition: 'ease-in 200ms',
            '& button': {
                opacity: 1,
                transition: 'ease-in 200ms',
            },
        },
        '& > *': {
            borderBottom: 'unset',
        },
        '& .MuiAccordionSummary-expandIcon': {
            alignSelf: 'baseline',
        },
    },
    tableCommentRow: {
        '& > *': {
            borderBottom: 'unset',
        },
        verticalAlign: 'top',
        height: '10px',
        cursor: 'pointer',
        '&:hover ': {
            backgroundColor: '#eeeeee',
            transition: 'ease-in 200ms',
            '& button': {
                opacity: 1,
                transition: 'ease-in 200ms',
            },
        },
    },
    center: {
        justifyContent: 'center',
    },
    editIcon: {
        opacity: 0,
        justifyContent: 'space-around',
    },
    textField: {
        fontSize: 14,
        fontWeight: 450,
        color: gray[600],
        lineHeight: '21px',
        wordWrap: 'break-word',
        width: '100%',
    },
    textFieldInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        padding: 8,
        borderRadius: 3,
        marginBottom: '0 !important',
        color: '#484848 !important',
    },
    amountInput: {
        textAlign: 'right',
    },
    readOnlyLabel: {
        fontSize: '14px',
        color: '#484848;',
        lineHeight: '20px',
    },
    readOnlyCommentLabel: {
        fontSize: '14px',
        color: '#484848;',
        lineHeight: '20px',
        padding: '10px 0px',
        display: 'block',
        wordBreak: 'break-word',
    },
    totalHoursWarning: {
        display: 'flex',
        alignItems: 'center',
        color: '#B71C1C',
        fontSize: '14px',
        margin: 10,
    },
    appointmentDetails: {
        padding: 0,
        margin: '0 10px',
    },
    appointmentType: {
        display: 'flex',
        color: '#484848',
        fontSize: 14,
        fontWeight: 'bold',
    },
    appointmentLicenses: {
        color: '#484848',
        fontSize: 14,
        fontWeight: 'bold',
    },
    root: {
        width: '100%',
    },
    accordionSummary: {
        flexDirection: 'row-reverse',
        padding: 0,
        '&:hover ': {
            backgroundColor: '#eeeeee',
            transition: 'ease-in 200ms',
            '& button': {
                opacity: 1,
                transition: 'ease-in 200ms',
            },
        },
        '& .MuiAccordionSummary-content': {
            margin: 0,
        },
    },
    accordionDetails: {
        '&:hover ': {
            backgroundColor: '#eeeeee',
            transition: 'ease-in 200ms',
            '& button': {
                opacity: 1,
                transition: 'ease-in 200ms',
            },
        },
    },
    containerChildPadding: {
        '& >*': {
            padding: '8px',
        },
    },
    tableHeaderContainer: {
        '& >*': {
            padding: '8px 5px',
        },
        marginLeft: '60px',
        width: 'calc(100% - 60px)',
    },
    eventTitle: {
        fontWeight: 500,
    },
    autoCompleteInputRoot: {
        maxHeight: '55px',
    },
    autoCompletePaper: {
        width: '200px',
    },
    alignHeaderColumns: {
        display: 'flex',
        justifyContent: 'flex-start',
    },
    accordionIcon: {
        padding: '5px 0px',
        width: '38px',
    },
    commentGrid: {
        padding: '0 8px',
    },
}));

const TimeSheetEntry: FunctionComponent<TimeSheetEntryProps> = (props) => {
    const {
        readOnly,
        hourLimit,
        selectedDate,
        timeSheetEntries,
        setIsMaxHourLimit,
        timeSheetCategories,
        handleTimeSheetEntry,
        timeSheetAppointments,
        timeEntryIdsWithComments,
        handleDeleteTimeSheetRow,
        isTimeSheetCategoriesLoading,
        isTimeSheetAppointmentsLoading,
    } = props;

    const classes = useStyles();

    const [comments, setComments] = useState<{ id: string; showComment: boolean }[]>([]);
    const [currentRow, setCurrentRow] = useState<{ index: number; expanded: boolean }>({ index: -1, expanded: false });

    useEffect(() => {
        if (timeEntryIdsWithComments.length)
            setComments(
                timeEntryIdsWithComments.map((commentId) => {
                    return {
                        id: commentId,
                        showComment: true,
                    };
                }),
            );
    }, [timeEntryIdsWithComments]);

    const Loader = <Skeleton width="50%" />;

    const totalHours = timeSheetEntries.reduce((acc, item) => {
        return acc + (!!item.unitHours ? +item.unitHours : 0);
    }, 0);

    setIsMaxHourLimit(totalHours > hourLimit);
    const handleCommentPanel = (id: string, type: string) => {
        switch (type) {
            case 'add':
                const isRowAlreadyAdded = comments.some((comment) => comment.id === id);
                !isRowAlreadyAdded && setComments([...comments, { id, showComment: true }]);
                break;
            case 'remove':
                const filteredComments = comments.filter((comment) => comment.id !== id);
                setComments(filteredComments);
                handleTimeSheetEntry(id, 'comments', '');
                break;
            default:
                break;
        }
    };

    const handleRowExpandCollapse = (index: number) => {
        if (index === currentRow.index) {
            //Just toggle the expanded boolean
            const expanded = !currentRow.expanded;
            setCurrentRow({ index: currentRow.index, expanded });
        } else {
            setCurrentRow({ index, expanded: true });
        }
    };
    const selectAppointment = (selectedEvent: Event | undefined) => {
        const selectedAppointmentValue = selectedEvent?.id
            ? {
                  label: getFullAddress(selectedEvent?.address),
                  value: selectedEvent?.id,
              }
            : null;
        return selectedAppointmentValue;
    };
    const selectPerson = (selectedEvent: Event | undefined, timeEntry: TimeSheetCard) => {
        const person = selectedEvent?.persons?.find((person) => person?.id === timeEntry?.person?.id);
        const selectedPersonValue = person?.id
            ? {
                  label: person?.name,
                  value: person?.id,
              }
            : null;
        return selectedPersonValue;
    };

    return (
        <Paper>
            <Typography className={classes.title} component="h1">
                {'Enter Time'}
            </Typography>

            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell style={{ padding: 0 }} className={classes.tableHeaderCell}>
                            <Grid container className={classes.tableHeaderContainer}>
                                <Grid item md={2} className={classes.alignHeaderColumns}>
                                    Category
                                </Grid>
                                <Grid item md={2} className={classes.alignHeaderColumns}>
                                    Appointment
                                </Grid>
                                <Grid item md={2} className={classes.alignHeaderColumns}>
                                    Account
                                </Grid>
                                <Grid item md={2} className={classes.alignHeaderColumns}>
                                    Start Time
                                </Grid>
                                <Grid item md={1} className={classes.alignHeaderColumns}>
                                    Hours
                                </Grid>
                                <Grid item md={1} className={classes.alignHeaderColumns}>
                                    Miles
                                </Grid>
                                <Grid item md={2} />
                            </Grid>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {timeSheetEntries.map((timeEntry, index) => {
                        const selectedEvent = timeSheetAppointments?.find(
                            (event: Event) => event.id === timeEntry?.serviceReference?.id,
                        );
                        const areAppointmentsAvailable = !!timeSheetCategories?.find(
                            (item) => item.id === timeEntry.category.id,
                        )?.invoiceType;
                        const isAppointmentSelected = !!selectedEvent && areAppointmentsAvailable;
                        const eventTitle =
                            selectedEvent &&
                            timeSheetAppointments.find((event) => event.id === selectedEvent.id)?.title?.trim();
                        const selectedAppointmentAddress = selectAppointment(selectedEvent);
                        const appointmentLocationWithTitle =
                            !!eventTitle || !!selectedAppointmentAddress ? (
                                <Box>
                                    {!!eventTitle && (
                                        <>
                                            {eventTitle}
                                            <br />
                                        </>
                                    )}
                                    {selectedAppointmentAddress?.label}
                                </Box>
                            ) : (
                                ''
                            );
                        const showCommentBox =
                             comments?.find((comment) => comment.id === timeEntry.key)?.showComment;

                        return (
                            <Fragment key={timeEntry.key}>
                                <TableRow className={classes.tableRow}>
                                    <TableCell style={{ padding: 0 }}>
                                        <div className={classes.root}>
                                            <Accordion
                                                expanded={
                                                    areAppointmentsAvailable &&
                                                    index === currentRow.index &&
                                                    currentRow.expanded
                                                }
                                                className={classes.accordionDetails}
                                                square
                                                variant="outlined"
                                            >
                                                <AccordionSummary
                                                    expandIcon={
                                                        isAppointmentSelected ? (
                                                            <IconButton
                                                                aria-label="expand row"
                                                                size="medium"
                                                                className={classes.accordionIcon}
                                                            >
                                                                <ExpandMoreIcon />
                                                            </IconButton>
                                                        ) : (
                                                            <Tooltip
                                                                disableFocusListener
                                                                disableTouchListener
                                                                TransitionComponent={Zoom}
                                                                title="No Appointment Selected"
                                                            >
                                                                <div>
                                                                    <IconButton
                                                                        aria-label="no-appointment-detail"
                                                                        size="medium"
                                                                        disabled={true}
                                                                        disableFocusRipple
                                                                        className={classes.accordionIcon}
                                                                    >
                                                                        <ExpandMoreIcon color="disabled" />
                                                                    </IconButton>
                                                                </div>
                                                            </Tooltip>
                                                        )
                                                    }
                                                    className={classes.accordionSummary}
                                                    aria-controls={`${timeEntry.key!}time-entry-sheet-content`}
                                                    id={`${timeEntry.key!}time-entry-sheet-header`}
                                                    IconButtonProps={{
                                                        onClick: () =>
                                                            isAppointmentSelected
                                                                ? handleRowExpandCollapse(index)
                                                                : undefined,
                                                    }}
                                                >
                                                    <Grid
                                                        container
                                                        className={classes.containerChildPadding}
                                                        justify="center"
                                                    >
                                                        <Grid item md={2}>
                                                            {isTimeSheetCategoriesLoading ? (
                                                                Loader
                                                            ) : readOnly ? (
                                                                <>
                                                                    <Typography className={classes.readOnlyLabel}>
                                                                        {
                                                                            timeSheetCategories.find(
                                                                                (timeSheetCategory) =>
                                                                                    timeSheetCategory.id ===
                                                                                    timeEntry?.category.id,
                                                                            )?.name
                                                                        }
                                                                    </Typography>
                                                                </>
                                                            ) : (
                                                                <AutoCompleteSelect
                                                                    data={timeSheetCategories.map(
                                                                        (category: TimeSheetCategory) => {
                                                                            const mileageRate = selectedDate
                                                                                ? category.mileageRates?.find(
                                                                                      (item) =>
                                                                                          (!item.startPeriod ||
                                                                                              item.startPeriod <=
                                                                                                  selectedDate) &&
                                                                                          (!item.endPeriod ||
                                                                                              item.endPeriod >=
                                                                                                  selectedDate),
                                                                                  )
                                                                                : undefined;
                                                                            return {
                                                                                label: mileageRate
                                                                                    ? `${category.name} - $${mileageRate.rate}/mile`
                                                                                    : category.name,
                                                                                value: category.id,
                                                                            };
                                                                        },
                                                                    )}
                                                                    onChange={(data: DropDownField) => {
                                                                        handleTimeSheetEntry(
                                                                            timeEntry.key,
                                                                            'category',
                                                                            data.value,
                                                                            index,
                                                                        );
                                                                        !areAppointmentsAvailable &&
                                                                            setCurrentRow({ index, expanded: false });
                                                                    }}
                                                                    inputSize={'small'}
                                                                    value={
                                                                        timeEntry?.category?.id
                                                                            ? {
                                                                                  label: timeEntry.category.name,
                                                                                  value: timeEntry.category.id,
                                                                              }
                                                                            : null
                                                                    }
                                                                    variant={'outlined'}
                                                                    disableClearable={true}
                                                                />
                                                            )}
                                                        </Grid>
                                                        <Grid item md={2}>
                                                            {isTimeSheetAppointmentsLoading ? (
                                                                Loader
                                                            ) : readOnly ? (
                                                                <Typography className={classes.readOnlyLabel}>
                                                                    {getFullAddress(
                                                                        timeSheetAppointments?.find(
                                                                            (appointment) =>
                                                                                appointment.id ===
                                                                                timeEntry?.serviceReference?.id,
                                                                        )?.address,
                                                                    )}
                                                                </Typography>
                                                            ) : areAppointmentsAvailable ? (
                                                                <Tooltip
                                                                    disableFocusListener
                                                                    disableTouchListener
                                                                    TransitionComponent={Zoom}
                                                                    title={appointmentLocationWithTitle}
                                                                >
                                                                    <Autocomplete
                                                                        options={timeSheetAppointments?.map(
                                                                            (appointment: Event) => ({
                                                                                label: getFullAddress(
                                                                                    appointment?.address,
                                                                                ),
                                                                                value: appointment?.id!,
                                                                            }),
                                                                        )}
                                                                        onChange={(e, data?: DropDownField) => {
                                                                            handleTimeSheetEntry(
                                                                                timeEntry.key,
                                                                                'appointment',
                                                                                data?.value,
                                                                                index,
                                                                            );
                                                                        }}
                                                                        value={
                                                                            selectAppointment(selectedEvent) ??
                                                                            undefined
                                                                        }
                                                                        disableClearable={true}
                                                                        classes={{
                                                                            inputRoot: classes.autoCompleteInputRoot,
                                                                            paper: classes.autoCompletePaper,
                                                                        }}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                aria-label="appointment-address"
                                                                                variant="outlined"
                                                                                size="small"
                                                                            />
                                                                        )}
                                                                        getOptionLabel={(data: any) =>
                                                                            data?.label || ''
                                                                        }
                                                                        renderOption={(data) => {
                                                                            const eventTitle = timeSheetAppointments
                                                                                .find(
                                                                                    (event) => event.id === data.value,
                                                                                )
                                                                                ?.title?.trim();
                                                                            return (
                                                                                <Typography variant="body2">
                                                                                    {!!eventTitle && (
                                                                                        <Typography
                                                                                            variant="body1"
                                                                                            className={
                                                                                                classes.eventTitle
                                                                                            }
                                                                                        >
                                                                                            {eventTitle}
                                                                                            <br />
                                                                                        </Typography>
                                                                                    )}
                                                                                    {data?.label}
                                                                                </Typography>
                                                                            );
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            ) : (
                                                                <></>
                                                            )}
                                                        </Grid>
                                                        <Grid item md={2}>
                                                            {isTimeSheetAppointmentsLoading ? (
                                                                Loader
                                                            ) : readOnly ? (
                                                                <Typography className={classes.readOnlyLabel}>
                                                                    {
                                                                        selectedEvent?.persons?.find(
                                                                            (person) =>
                                                                                person.id === timeEntry.person?.id,
                                                                        )?.name
                                                                    }
                                                                </Typography>
                                                            ) : areAppointmentsAvailable ? (
                                                                <AutoCompleteSelect
                                                                    data={
                                                                        selectedEvent?.persons?.length
                                                                            ? selectedEvent?.persons.flatMap(
                                                                                  (person: {
                                                                                      id: string;
                                                                                      name: string;
                                                                                  }) => {
                                                                                      return {
                                                                                          label: person.name,
                                                                                          value: person.id,
                                                                                      };
                                                                                  },
                                                                              )
                                                                            : []
                                                                    }
                                                                    onChange={(data: DropDownField) => {
                                                                        handleTimeSheetEntry(
                                                                            timeEntry.key,
                                                                            'person',
                                                                            data.value,
                                                                            index,
                                                                        );
                                                                    }}
                                                                    inputSize={'small'}
                                                                    value={selectPerson(selectedEvent, timeEntry)}
                                                                    variant={'outlined'}
                                                                    disableClearable={true}
                                                                />
                                                            ) : (
                                                                <></>
                                                            )}
                                                        </Grid>
                                                        <Grid item md={2}>
                                                            {readOnly ? (
                                                                <Typography className={classes.readOnlyLabel}>
                                                                    {timeEntry.startTime &&
                                                                        moment(
                                                                            `${selectedDate} ${timeEntry.startTime}`,
                                                                        ).format('LT')}
                                                                </Typography>
                                                            ) : (
                                                                <TimePicker
                                                                    value={timeEntry.startTime || ''}
                                                                    onChange={(time: any) =>
                                                                        handleTimeSheetEntry(
                                                                            timeEntry.key,
                                                                            'startTime',
                                                                            time,
                                                                            index,
                                                                        )
                                                                    }
                                                                    disabled={!timeEntry.category.id}
                                                                />
                                                            )}
                                                        </Grid>
                                                        <Grid item md={1}>
                                                            {readOnly ? (
                                                                <Typography className={classes.readOnlyLabel}>
                                                                    {timeEntry?.unitHours}
                                                                </Typography>
                                                            ) : (
                                                                <InputTextComponent
                                                                    disabled={!timeEntry.category.id}
                                                                    id={`hours-${timeEntry.key}`}
                                                                    size="small"
                                                                    fontSize="13"
                                                                    className={classes.textField}
                                                                    inputProps={classNames(classes.textFieldInput)}
                                                                    onChange={(hours: string) =>
                                                                        handleTimeSheetEntry(
                                                                            timeEntry.key,
                                                                            'unitHours',
                                                                            hours,
                                                                            index,
                                                                        )
                                                                    }
                                                                    value={timeEntry?.unitHours || ''}
                                                                    inputType={'number'}
                                                                    error={totalHours > hourLimit}
                                                                />
                                                            )}
                                                        </Grid>
                                                        <Grid item md={1}>
                                                            {timeSheetCategories.find(
                                                                (item) =>
                                                                    item.id === timeEntry.category.id &&
                                                                    item.mileageRates,
                                                            ) &&
                                                                (readOnly ? (
                                                                    <Typography className={classes.readOnlyLabel}>
                                                                        {timeEntry?.mileage}
                                                                    </Typography>
                                                                ) : (
                                                                    <InputTextComponent
                                                                        id={`mileage-${timeEntry.key}`}
                                                                        size="small"
                                                                        fontSize="13"
                                                                        className={classes.textField}
                                                                        inputProps={classes.textFieldInput}
                                                                        onChange={(miles: string) =>
                                                                            handleTimeSheetEntry(
                                                                                timeEntry.key,
                                                                                'mileage',
                                                                                miles,
                                                                                index,
                                                                            )
                                                                        }
                                                                        inputType={'number'}
                                                                        value={timeEntry?.mileage || ''}
                                                                    />
                                                                ))}
                                                        </Grid>
                                                        <Grid item md={2}>
                                                            {!readOnly && (
                                                                <Box
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignContent: 'center',
                                                                        alignItems: 'center',
                                                                    }}
                                                                >
                                                                    <Tooltip
                                                                        disableFocusListener
                                                                        disableTouchListener
                                                                        TransitionComponent={Zoom}
                                                                        title="Add Comment"
                                                                    >
                                                                        <IconButton
                                                                            onClick={() => {
                                                                                handleCommentPanel(
                                                                                    timeEntry.key!,
                                                                                    'add',
                                                                                );
                                                                            }}
                                                                            className={classes.editIcon}
                                                                        >
                                                                            <AddCommentOutlinedIcon />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    {index !== timeSheetEntries.length - 1 ? (
                                                                        <Tooltip
                                                                            disableFocusListener
                                                                            disableTouchListener
                                                                            TransitionComponent={Zoom}
                                                                            title="Delete"
                                                                        >
                                                                            <IconButton
                                                                                onClick={(e: MouseEvent<HTMLElement>) =>
                                                                                    handleDeleteTimeSheetRow(
                                                                                        timeEntry.key,
                                                                                        e,
                                                                                    )
                                                                                }
                                                                                className={classes.editIcon}
                                                                            >
                                                                                <Delete />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    ) : null}
                                                                </Box>
                                                            )}
                                                        </Grid>
                                                        {showCommentBox ? (
                                                            <Grid item md={12} className={classes.commentGrid}>
                                                                {!readOnly ? (
                                                                    <Collapse
                                                                        in={showCommentBox}
                                                                        timeout="auto"
                                                                        unmountOnExit
                                                                    >
                                                                        <Box display="flex" alignItems="center">
                                                                            <InputTextComponent
                                                                                id={`comment-${timeEntry.key}`}
                                                                                size="small"
                                                                                fontSize="13"
                                                                                className={classes.textField}
                                                                                inputProps={classes.textFieldInput}
                                                                                onChange={(comments: string) => {
                                                                                    handleTimeSheetEntry(
                                                                                        timeEntry.key,
                                                                                        'comments',
                                                                                        comments,
                                                                                        index,
                                                                                    );
                                                                                }}
                                                                                value={timeEntry?.comments}
                                                                            />

                                                                            <IconButton
                                                                                title="Close"
                                                                                style={{ color: 'grey' }}
                                                                                onClick={() => {
                                                                                    handleCommentPanel(
                                                                                        timeEntry.key!,
                                                                                        'remove',
                                                                                    );
                                                                                }}
                                                                            >
                                                                                <CloseIcon />
                                                                            </IconButton>
                                                                        </Box>
                                                                    </Collapse>
                                                                ) : timeEntry?.comments ? (
                                                                    <Typography
                                                                        className={classes.readOnlyCommentLabel}
                                                                    >
                                                                        {timeEntry?.comments}
                                                                    </Typography>
                                                                ) : (
                                                                    <></>
                                                                )}
                                                            </Grid>
                                                        ) : (
                                                            <></>
                                                        )}
                                                    </Grid>
                                                </AccordionSummary>
                                                <AccordionDetails className={classes.appointmentDetails}>
                                                    {isAppointmentSelected && (
                                                        <Grid
                                                            container
                                                            justify="flex-start"
                                                            className={classes.containerChildPadding}
                                                            style={{ marginLeft: '3.5%' }}
                                                        >
                                                            <Grid item md={12}>
                                                                <Box className={classes.appointmentType}>
                                                                    {selectedEvent?.type && (
                                                                        <>
                                                                            <Box fontWeight={'bold'}>
                                                                                Appointment Type:
                                                                            </Box>
                                                                            <Box fontWeight={400}>
                                                                                &nbsp;{selectedEvent.type.name}
                                                                            </Box>
                                                                        </>
                                                                    )}
                                                                </Box>
                                                                <Box className={classes.appointmentType}>
                                                                    {selectedEvent?.inspectionType && (
                                                                        <>
                                                                            <Box fontWeight={'bold'}>
                                                                                Inspection Type:{' '}
                                                                            </Box>
                                                                            <Box fontWeight={400}>
                                                                                &nbsp;
                                                                                {selectedEvent.inspectionType.name}
                                                                            </Box>
                                                                        </>
                                                                    )}
                                                                </Box>
                                                                <Box className={classes.appointmentLicenses}>
                                                                    {!!selectedEvent?.licenses?.length && (
                                                                        <>
                                                                            <Box fontWeight={'bold'}>Licenses: </Box>
                                                                            <Grid container spacing={1}>
                                                                                {selectedEvent.licenses.map(
                                                                                    (license) => (
                                                                                        <Grid
                                                                                            item
                                                                                            md={3}
                                                                                            key={license.licenseNumber}
                                                                                        >
                                                                                            <Box fontWeight={400}>
                                                                                                {license?.type?.name}
                                                                                                &nbsp;
                                                                                                {license?.licenseNumber}
                                                                                            </Box>
                                                                                        </Grid>
                                                                                    ),
                                                                                )}
                                                                            </Grid>
                                                                        </>
                                                                    )}
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            </Fragment>
                        );
                    })}
                </TableBody>
            </Table>

            {totalHours > hourLimit && (
                <Typography className={classes.totalHoursWarning}>
                    <ErrorOutlineIcon />
                    The total time exceeds the daily limit.
                </Typography>
            )}
        </Paper>
    );
};

export default TimeSheetEntry;
